"use client";

import React, { useState } from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader, ArrowRight, CircleCheck } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function InquiryPortal() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="153016" id="start-your-journey" className="py-24 bg-background text-foreground relative overflow-hidden" ref={ref}>
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-secondary/10 rounded-lg blur-3xl pointer-events-none" />

      <div className={`container mx-auto px-4 max-w-4xl relative z-10 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-6xl font-serif tracking-tight text-foreground mb-4">Experiences</h2>
          <p className="text-muted-foreground font-sans text-lg max-w-xl mx-auto">
            Don’t just travel, PS Travel. Connect with our Traveler Care Team to curate your next immersive experience.
          </p>
        </div>

        <div className="bg-background/60 text-card-foreground rounded-sm shadow-xl border border-border/30 p-6 md:p-10 backdrop-blur-md">
          <FormProvider formKey="start_journey_form_curation" formId="start_journey_form_curation" sectionName="Inquiry Portal" defaultValues={{
          full_name: "",
          email: "",
          phone: "",
          party_size: "",
          travel_dates: "",
          special_requests: ""
        }} validationRules={{
          full_name: {
            required: true
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            required: true
          },
          party_size: {
            required: true
          },
          travel_dates: {
            required: true
          },
          special_requests: {
            required: false
          }
        }} submitText="Submit My Request" successMessage="Your request has been received. Our team will be in touch shortly to begin crafting your journey.">
            <MultiStepForm />
          </FormProvider>
          
          <div className="mt-8 text-center border-t border-border/20 pt-8">
            <p className="text-xs font-mono tracking-widest text-muted-foreground uppercase">
              Every journey begins with a conversation.
            </p>
          </div>
        </div>
      </div>
    </section>;
}
function MultiStepForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const [activeStep, setActiveStep] = useState("step-1");
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in zoom-in duration-500">
        <div className="size-16 bg-primary/10 rounded-lg flex items-center justify-center mb-6">
          <CircleCheck className="size-8 text-primary" />
        </div>
        <h3 className="text-3xl font-serif mb-2 text-secondary">Journey Initiated</h3>
        <p className="text-muted-foreground font-sans mb-8 max-w-md">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="rounded-sm px-8 border-secondary text-secondary hover:bg-secondary hover:text-secondary-foreground transition-colors duration-300">
          Plan Another Discovery
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <Tabs value={activeStep} onValueChange={setActiveStep} className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-10 bg-muted/30 p-1 rounded-sm">
          <TabsTrigger value="step-1" className="rounded-sm font-mono text-[10px] md:text-xs uppercase tracking-wider data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            1. Profile
          </TabsTrigger>
          <TabsTrigger value="step-2" className="rounded-sm font-mono text-[10px] md:text-xs uppercase tracking-wider data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            2. Discovery
          </TabsTrigger>
        </TabsList>

        <div className="min-h-[320px]">
          <TabsContent value="step-1" className="space-y-6 mt-0 animate-in fade-in slide-in-from-right-4 duration-300">
            <div className="space-y-4">
              <h3 className="text-2xl font-serif text-secondary">Tell us about yourself</h3>
              <p className="text-sm font-sans text-muted-foreground">Provide your contact details so our care team can reach you.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField control={control} name="full_name" rules={{
              required: "Full name is required"
            }} render={({
              field
            }) => <FormItem className="md:col-span-2">
                    <FormLabel className="font-mono text-xs uppercase tracking-widest text-primary">Your Full Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Mark Valencia" className="h-12 bg-transparent border-0 border-b border-secondary/50 focus-visible:ring-0 focus-visible:border-secondary transition-all" {...field} />
                    </FormControl>
                    <FormDescription className="text-[10px] font-sans opacity-60">As it appears on your travel documents</FormDescription>
                    <FormMessage className="text-xs font-sans" />
                  </FormItem>} />

              <FormField control={control} name="email" rules={{
              required: "Email is required",
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Invalid email address"
              }
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel className="font-mono text-xs uppercase tracking-widest text-primary">Email Address</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="explorer@youtravelps.com" className="h-12 bg-transparent border-0 border-b border-secondary/50 focus-visible:ring-0 focus-visible:border-secondary transition-all" {...field} />
                    </FormControl>
                    <FormDescription className="text-[10px] font-sans opacity-60">For itinerary delivery</FormDescription>
                    <FormMessage className="text-xs font-sans" />
                  </FormItem>} />

              <FormField control={control} name="phone" rules={{
              required: "Phone number is required"
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel className="font-mono text-xs uppercase tracking-widest text-primary">Phone Number</FormLabel>
                    <FormControl>
                      <Input type="tel" placeholder="+1 (555) 000-0000" className="h-12 bg-transparent border-0 border-b border-secondary/50 focus-visible:ring-0 focus-visible:border-secondary transition-all" {...field} />
                    </FormControl>
                    <FormDescription className="text-[10px] font-sans opacity-60">Include international country code</FormDescription>
                    <FormMessage className="text-xs font-sans" />
                  </FormItem>} />

              <FormField control={control} name="party_size" rules={{
              required: "Party size is required"
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel className="font-mono text-xs uppercase tracking-widest text-primary">Party Size</FormLabel>
                    <FormControl>
                      <Input type="number" placeholder="2" className="h-12 bg-transparent border-0 border-b border-secondary/50 focus-visible:ring-0 focus-visible:border-secondary transition-all" {...field} />
                    </FormControl>
                    <FormDescription className="text-[10px] font-sans opacity-60">Number of travelers</FormDescription>
                    <FormMessage className="text-xs font-sans" />
                  </FormItem>} />

              <FormField control={control} name="travel_dates" rules={{
              required: "Travel dates are required"
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel className="font-mono text-xs uppercase tracking-widest text-primary">Travel Dates</FormLabel>
                    <FormControl>
                      <Input placeholder="Summer 2025 or specific dates" className="h-12 bg-transparent border-0 border-b border-secondary/50 focus-visible:ring-0 focus-visible:border-secondary transition-all" {...field} />
                    </FormControl>
                    <FormDescription className="text-[10px] font-sans opacity-60">When do you plan to travel?</FormDescription>
                    <FormMessage className="text-xs font-sans" />
                  </FormItem>} />
            </div>

            <div className="flex justify-end pt-6">
              <Button type="button" onClick={() => setActiveStep("step-2")} className="rounded-sm px-10 bg-secondary hover:bg-secondary/90 text-secondary-foreground transition-all duration-300">
                Continue <ArrowRight className="ml-2 size-4" />
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="step-2" className="space-y-6 mt-0 animate-in fade-in slide-in-from-right-4 duration-300">
            <div className="space-y-4">
              <h3 className="text-2xl font-serif text-secondary">Special Requests & Vision</h3>
              <p className="text-sm font-sans text-muted-foreground">Let us know any specific visions or requirements for your journey.</p>
            </div>

            <div className="grid grid-cols-1 gap-8">
              <FormField control={control} name="special_requests" render={({
              field
            }) => <FormItem className="flex flex-col">
                    <FormLabel className="font-mono text-xs uppercase tracking-widest text-primary">Your Vision & Requests</FormLabel>
                    <FormControl>
                      <textarea className="flex min-h-[220px] w-full border-0 border-b border-secondary bg-transparent px-3 py-2 text-sm shadow-xs placeholder:text-muted-foreground/50 focus-visible:outline-none focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 resize-none transition-all" placeholder="Share your specific interests, dietary needs, or philanthropic goals..." {...field} />
                    </FormControl>
                    <FormDescription className="text-[10px] font-sans opacity-60">The more detail you provide, the deeper we can curate your discovery.</FormDescription>
                    <FormMessage className="text-xs font-sans" />
                  </FormItem>} />
            </div>

            {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-sm text-destructive text-sm font-sans">
                {submitError}
              </div>}

            <div className="flex justify-between pt-6 border-t border-border/20">
              <Button type="button" variant="ghost" onClick={() => setActiveStep("step-1")} className="rounded-sm font-mono text-xs uppercase tracking-widest hover:text-secondary transition-colors">
                Back
              </Button>
              <Button type="submit" disabled={isSubmitting} className="rounded-sm px-12 min-w-[200px] bg-secondary hover:bg-secondary/90 text-secondary-foreground font-mono text-sm uppercase tracking-widest transition-all duration-500 shadow-lg shadow-secondary/20">
                {isSubmitting ? <>
                    <Loader className="animate-spin mr-2 size-4" />
                    Transmitting...
                  </> : "Submit My Request"}
              </Button>
            </div>
          </TabsContent>
        </div>
      </Tabs>
    </Form>;
}